import { Lock } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";

/**
 * Per-cell lock toggle for the workpaper grid (MS-2533).
 *
 * Locking a workpaper cell blocks it being overwritten by reruns. An unlocked
 * cell reveals an outline lock on hover — the cell wrapper must carry the
 * `group/cell` class (named group: `CheckDecision`'s wrapper already uses the
 * default `group` for other reveals). A locked cell shows a filled lock at all
 * times so protection reads at a glance. Positioned absolutely; the wrapper
 * must also be `relative`.
 */
export const CellLockButton = ({
  lockedBy,
  onToggle,
  disabled = false,
}: {
  lockedBy: string | null | undefined;
  onToggle: () => void;
  disabled?: boolean;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        aria-label={lockedBy ? "Unlock cell" : "Lock cell"}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        // No z-index: the grid's sample column is sticky with z-10, and a
        // z-indexed icon in a cell scrolled beneath it would paint through.
        // Being absolutely positioned already places it above the cell's own
        // static content.
        className={`absolute right-1 top-1 rounded p-0.5 transition-opacity ${
          lockedBy
            ? "opacity-100 text-stone-400 hover:text-stone-600"
            : "opacity-0 text-stone-400 hover:text-stone-600 focus-visible:opacity-100 group-hover/cell:opacity-100"
        } ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
      >
        {/* Filled = body rect only; filling the whole icon would also fill the
            shackle's arc into a solid blob and lose the lock's cutout. */}
        <Lock
          className={`h-3.5 w-3.5 ${lockedBy ? "[&>rect]:fill-current" : ""}`}
        />
      </button>
    </TooltipTrigger>
    <TooltipContent>
      <p>
        {lockedBy
          ? "Unlock — allow reruns to overwrite this cell"
          : "Lock — block reruns from overwriting this cell"}
      </p>
    </TooltipContent>
  </Tooltip>
);
